import type { ContentType, Distribution } from "./content";
import type { OriginalItemTypes } from "./session";

export type Content = {
	id: string;
	type: ContentType;
	collectionType?: "STUDY_PACK" | "PRACTICE_TEST";
	distribution: Distribution;
	totalCount: number;
};

export type DisplayType = ["Filter" | "Progress" | "Display"];
export type CurrentView = "Filter" | "Progress" | "Display";
export type Item = {
	id: number;
	type: "flashcard" | "question";
	distribution: keyof Distribution;
	variant?: OriginalItemTypes;
	stats?: {
		score?: number;
		speed?: number;
		accuracy?: number;
		timeTaken?: number | null;
		confidence?: number | null;
		numDistractors?: number | null;
		numPointsEarned?: number;
		numTotalPoints?: number;
	};
};
export interface MixedItemTag {
	id: number;
	title: string;
	code?: string;
	type: string;
	label?: string;
	description?: string;
	displayType?: DisplayType;
	iconUrl?: string;
	icon?: string | { name?: string; color?: string } | null;
	distribution: Distribution;
	distributionKey?: string;
	locked?: boolean;
	children?: MixedItemTag[];
	weight?: number;
	items?: Item[];
	numItems: number;
	numCorrect?: number;
	numIncorrect?: number;
	numSkipped?: number;
	score?: number;
	averageScore?: number;
	numTotalPoints?: number;
	numPointsEarned?: number;
	colorScale?: string[];
	sortOrder?: number;
	count?: number;
	totalResponses: number;
	mastery?: {
		value: number | null;
		scoreBasis: "PREDICTED_MASTERY" | "CORRECTNESS" | "POINTS" | "MIXED" | null;
		responseScope: "FORMATIVE_AND_COMPLETED_SUMMATIVE";
		maxWindowSize: number;
		sampleSize: number;
		distinctItemCount: number;
		minimumEvidence: number;
		status: "NO_EVIDENCE" | "PROVISIONAL" | "ELIGIBLE";
		windowComplete: boolean;
		lastAnsweredAt?: string;
		calculationVersion: number;
	};
}

export type LegacyReport = {
	id: string;
	numItems: number;
	numericId: number;
	score: number;
	sessionDate: string;
};

export type ExamStatus =
	| "COMPLETE"
	| "ACTIVE"
	| "NOT_STARTED"
	| "LEGACYREPORT"
	| "MIGRATEDREPORT";

export type ExamSubType = "PRACTICE_TEST" | "DIAGNOSTIC";
export type PracticeTestExam = {
	id: string;
	title: string;
	numItems: number;
	numCompleted: number;
	createdAt: string;
	collectionId: number;
	status?: ExamStatus;
	totalTime: number;
	numericId?: number;
	dateCompleted: string;
	timeElapsed: number;
	distribution: Distribution;
	attempted: Distribution;
	hasAccess?: boolean;
	subType: ExamSubType;
	type?: "SUMMATIVE" | "FORMATIVE";
	legacyReport?: LegacyReport;
	migratedSessionReport?: LegacyReport;
	beginStartDate?: string;
	endStartDate?: string;
	icon?: string;
	examName?: string;
	numAttemptedItems?: number;
	numPointsEarned?: number;
	numPointsTotal?: number;
	tagDetails?: MixedItemTag[] | null;
	topics?: string[] | null;
	topicsFilterContext?: string[] | null;
};

export type RecommendationContextAxisRole =
	| "TRACK"
	| "PROGRESSION"
	| "DIMENSION";

export type RecommendationContextAxis = {
	id: string;
	title: string;
	role: RecommendationContextAxisRole;
	values: Array<{
		id: number;
		title: string;
		sortOrder: number;
	}>;
};

export type RecommendationContextCell = {
	key: string;
	coordinates: Array<{ axisId: string; valueId: number }>;
	launch: {
		availableItemCount: number;
		distribution: Distribution;
		tagGroups: number[][];
	};
	evidence: {
		totalResponses: number;
		distinctItemCount: number;
		lastAnsweredAt: string | null;
		rollingMastery10: number | null;
		rollingMastery20: number | null;
		rollingMastery30: number | null;
	};
};

export type RecommendationContext = {
	schemaVersion: 2;
	calculationVersion: number;
	generatedAt: string;
	exam: {
		id: string;
		numericId: number;
		title: string;
	};
	axes: RecommendationContextAxis[];
	policy: {
		rollingWindows: [10, 20, 30];
		minimumEvidence: number;
		passingMastery: number;
		rollupAxisSets: string[][];
		targetBarrier: {
			requiresAllTracks: true;
		};
	};
	cells: RecommendationContextCell[];
};

export type MixedItemDist = {
	iconUrl: string;
	title: string;
	contents?: Content[];
	freeTagIds: number[];
	tags: MixedItemTag[];
	exams?: PracticeTestExam[];
	hasAccess?: boolean;
	accuracy?: number;
	totalResponses: number;
	recommendationContext?: RecommendationContext;
	/** Titles of skills gated behind purchase. Returned only when the user lacks access. */
	premiumSkills?: string[];
};

export type TagsByType = Record<string, MixedItemTag[]>;
